import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Calendar, Send, Check, AlertCircle, Loader2 } from 'lucide-react';
import Reveal from '../components/Reveal';
import { bookingUrl, images, contactInfo } from '../data/mock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { firstName: '', lastName: '', email: '', phone: '', message: '', rgpd: false, website: '' };
const FIELD_LABELS = { first_name: 'Prénom', last_name: 'Nom', email: 'Email', phone: 'Téléphone', message: 'Message' };

const errorMessage = (detail, fallback, status) => {
  if (status === 429) return 'Trop de demandes envoyées. Merci de réessayer un peu plus tard ou de nous appeler.';
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail) && detail.length) {
    const field = FIELD_LABELS[detail[0]?.loc?.[detail[0].loc.length - 1]];
    return field ? `Le champ « ${field} » est invalide ou trop court.` : 'Certains champs sont invalides. Merci de vérifier votre saisie.';
  }
  return fallback;
};

const successMessage = (data) => {
  if (!data.email_sent) return 'Message bien reçu. Nous revenons vers vous rapidement.';
  const ack = data.ack_sent ? ' Un accusé de réception vous a été adressé par email.' : '';
  return `Message envoyé avec succès.${ack} Nous revenons vers vous sous 24 heures ouvrées.`;
};

const toPayload = (form) => ({ first_name: form.firstName, last_name: form.lastName, email: form.email, phone: form.phone, message: form.message, website: form.website });

const useContactSubmit = (form, onSuccess) => {
  const [status, setStatus] = useState({ state: 'idle', msg: '' });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.rgpd) { setStatus({ state: 'error', msg: 'Merci d’accepter le traitement de vos données.' }); return; }
    setStatus({ state: 'loading', msg: '' });
    try {
      const { data } = await axios.post(`${API}/contact`, toPayload(form));
      setStatus({ state: 'success', msg: successMessage(data) });
      onSuccess();
    } catch (err) {
      setStatus({ state: 'error', msg: errorMessage(err?.response?.data?.detail, 'Impossible d’envoyer le message. Réessayez ou contactez-nous par email.', err?.response?.status) });
    }
  };
  return { status, submit };
};

const ContactHero = () => (
  <section className="relative pt-40 pb-24 bg-[#010c23] text-white overflow-hidden">
    <div className="absolute inset-0 opacity-25">
      <img src={images.heroAlt} alt="" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#010c23]/60 to-[#010c23]" />
    </div>
    <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12">
      <Reveal>
        <div className="flex items-center gap-3 mb-6">
          <span className="h-px w-14 bg-[#ff7300]" />
          <span className="text-[11px] tracking-[0.35em] uppercase text-[#ff7300] font-medium">Contact</span>
        </div>
        <h1 className="font-serif text-5xl md:text-7xl font-light leading-tight max-w-4xl">
          Parlons de vos <span className="italic text-[#ff7300]">objectifs</span>
        </h1>
        <p className="mt-8 text-lg text-white/85 max-w-2xl">Un premier échange pour comprendre votre situation et donner un cap à vos projets.</p>
      </Reveal>
    </div>
  </section>
);

const ChannelCard = ({ href, icon: Icon, label, value, testId, external }) => (
  <a href={href} target={external ? '_blank' : undefined} rel={external ? 'noreferrer' : undefined} className="flex items-start gap-4 p-5 bg-white border border-black/5 hover:border-[#ff7300] transition-colors" data-testid={testId}>
    <span className="w-10 h-10 flex items-center justify-center bg-[#ff7300]/10 text-[#ff7300]"><Icon size={18} /></span>
    <span>
      <p className="text-xs uppercase tracking-widest text-[#010c23]/50">{label}</p>
      <p className="font-medium text-[#010c23] mt-1">{value}</p>
    </span>
  </a>
);

const ContactChannels = () => (
  <Reveal className="lg:col-span-5">
    <div className="section-label mb-3"><span className="divider-gold"></span>Nous joindre</div>
    <h2 className="font-serif text-4xl text-[#010c23] font-light leading-tight">À votre disposition, en toute confidentialité</h2>
    <p className="mt-6 text-[#010c23]/70 leading-relaxed">Choisissez le canal qui vous convient. Nous vous répondons sous 24 heures ouvrées.</p>
    <div className="mt-10 space-y-5 no-justify">
      <ChannelCard href={`mailto:${contactInfo.email}`} icon={Mail} label="E-mail" value={contactInfo.email} testId="contact-email" />
      <ChannelCard href={`tel:${contactInfo.phone.replace(/\s/g, '')}`} icon={Phone} label="Téléphone" value={contactInfo.phone} testId="contact-phone" />
      <div className="flex items-start gap-4 p-5 bg-white border border-black/5" data-testid="contact-address">
        <span className="w-10 h-10 flex items-center justify-center bg-[#ff7300]/10 text-[#ff7300]"><MapPin size={18} /></span>
        <span>
          <p className="text-xs uppercase tracking-widest text-[#010c23]/50">Adresses</p>
          <p className="text-[#010c23] mt-1"><span className="text-xs uppercase tracking-widest text-[#010c23]/50 mr-2">Siège social et correspondance</span><span className="font-medium">{contactInfo.headOffice}</span></p>
          <p className="text-[#010c23] mt-1"><span className="text-xs uppercase tracking-widest text-[#010c23]/50 mr-2">Bureau d’accueil</span><span className="font-medium">{contactInfo.office}</span></p>
        </span>
      </div>
      <a href={bookingUrl} target="_blank" rel="noreferrer" className="flex items-start gap-4 p-5 bg-[#010c23] text-white hover:bg-[#ff7300] transition-colors" data-testid="contact-booking">
        <span className="w-10 h-10 flex items-center justify-center bg-white/10"><Calendar size={18} /></span>
        <span>
          <p className="text-xs uppercase tracking-widest text-white/70">Agenda en ligne</p>
          <p className="font-medium mt-1">Réserver un créneau en ligne avec un conseiller</p>
        </span>
      </a>
    </div>
  </Reveal>
);

const Field = ({ label, type = 'text', value, onChange, minLength }) => (
  <div>
    <label className="text-xs uppercase tracking-widest text-[#010c23]/60">{label}</label>
    <input required type={type} value={value} onChange={onChange} minLength={minLength} className="mt-2 w-full border-b border-black/20 focus:border-[#ff7300] outline-none py-3 text-[#010c23] bg-transparent" />
  </div>
);

const SubmitLabel = ({ state }) => {
  if (state === 'loading') return <><Loader2 size={18} className="animate-spin" /> Envoi…</>;
  if (state === 'success') return <><Check size={18} /> Message envoyé</>;
  return <>Envoyer <Send size={18} /></>;
};

const StatusMessage = ({ status }) => {
  if (status.state === 'success') {
    return (
      <div className="mt-5 flex items-start gap-2 text-sm text-[#010c23]/80 bg-[#ff7300]/10 border border-[#ff7300]/30 p-4" data-testid="contact-success">
        <Check size={16} className="mt-0.5 text-[#ff7300]" /><span>{status.msg}</span>
      </div>
    );
  }
  if (status.state === 'error') {
    return (
      <div className="mt-5 flex items-start gap-2 text-sm text-red-800 bg-red-50 border border-red-200 p-4" data-testid="contact-error">
        <AlertCircle size={16} className="mt-0.5" /><span>{status.msg}</span>
      </div>
    );
  }
  return null;
};

const ContactForm = () => {
  const [form, setForm] = useState(EMPTY);
  const { status, submit } = useContactSubmit(form, () => setForm(EMPTY));
  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: k === 'rgpd' ? e.target.checked : e.target.value }));

  return (
    <form onSubmit={submit} className="bg-white border border-black/5 p-8 md:p-12" data-testid="contact-form">
      <h3 className="font-serif text-2xl text-[#010c23]">Nous écrire</h3>
      <p className="text-sm text-[#010c23]/60 mt-2">Tous les champs sont requis. Vos informations restent confidentielles.</p>
      <div className="grid md:grid-cols-2 gap-5 mt-8">
        <input type="text" name="website" value={form.website} onChange={onChange('website')} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
        <Field label="Prénom" value={form.firstName} onChange={onChange('firstName')} />
        <Field label="Nom" value={form.lastName} onChange={onChange('lastName')} />
        <Field label="E-mail" type="email" value={form.email} onChange={onChange('email')} />
        <Field label="Téléphone" type="tel" value={form.phone} onChange={onChange('phone')} minLength={4} />
      </div>
      <div className="mt-5">
        <label className="text-xs uppercase tracking-widest text-[#010c23]/60">Message</label>
        <textarea required minLength={5} maxLength={5000} value={form.message} onChange={onChange('message')} rows={5} className="mt-2 w-full border-b border-black/20 focus:border-[#ff7300] outline-none py-3 text-[#010c23] resize-none bg-transparent" placeholder="Décrivez brièvement votre besoin..." />
      </div>
      <div className="flex items-start gap-3 mt-6">
        <input type="checkbox" required id="rgpd" checked={form.rgpd} onChange={onChange('rgpd')} className="mt-1 accent-[#ff7300]" />
        <label htmlFor="rgpd" className="text-xs text-[#010c23]/60 leading-relaxed">J’accepte que mes données soient utilisées pour recontact selon la <Link to="/reglementaire#confidentialite" className="text-[#ff7300] hover:underline" data-testid="contact-privacy-link">politique de confidentialité</Link> d’Ometis Patrimoine.</label>
      </div>
      <button type="submit" disabled={status.state === 'loading'} className="btn-primary mt-8 disabled:opacity-70" data-testid="contact-submit-button">
        <SubmitLabel state={status.state} />
      </button>
      <StatusMessage status={status} />
    </form>
  );
};

const Contact = () => (
  <div>
    <ContactHero />
    <section className="py-24 bg-[#fbf9f5]">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
        <ContactChannels />
        <Reveal className="lg:col-span-7" delay={120}>
          <ContactForm />
        </Reveal>
      </div>
    </section>
  </div>
);

export default Contact;
